import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Typography } from '@/components/ui/typography'
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'

function getAttemptElapsedSeconds(attempt, nowMs = Date.now()) {
  if (!attempt?.queued_at && !attempt?.started_at) {
    return 0
  }

  const startValue = attempt.started_at ?? attempt.queued_at
  const startMs = Date.parse(startValue)
  const endMs = attempt.finished_at ? Date.parse(attempt.finished_at) : nowMs

  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs < startMs) {
    return 0
  }

  return Math.floor((endMs - startMs) / 1000)
}

function formatAttemptElapsedTime(totalSeconds) {
  const seconds = Math.max(0, Number(totalSeconds) || 0)
  const minutes = Math.floor(seconds / 60)
  const remainder = seconds % 60

  if (minutes < 1) {
    return `${remainder}s`
  }

  return `${minutes}m ${String(remainder).padStart(2, '0')}s`
}

function useAttemptElapsedSeconds(attempt) {
  const [nowMs, setNowMs] = useState(() => Date.now())
  const running = attempt?.status === 'queued' || attempt?.status === 'running'

  useEffect(() => {
    if (!running) {
      return
    }

    const intervalId = window.setInterval(() => {
      setNowMs(Date.now())
    }, 1000)

    return () => window.clearInterval(intervalId)
  }, [running])

  return getAttemptElapsedSeconds(attempt, nowMs)
}

function getStageStatusLabel(status) {
  return (
    {
      completed: 'Complete',
      current: 'In progress',
      failed: 'Failed',
      pending: 'Waiting',
    }[status] ?? 'Waiting'
  )
}

function AttemptTimeline({ attempt }) {
  const stages = attempt?.stages ?? []

  if (stages.length === 0) {
    return null
  }

  return (
    <div className="divide-y divide-border/70 border-y border-border/70">
      {stages.map((stage) => (
        <div
          key={stage.key}
          className="grid gap-3 py-3 @3xl/fsn-status:grid-cols-[minmax(0,1fr)_minmax(7rem,auto)_minmax(9rem,auto)] @3xl/fsn-status:items-center"
        >
          <div className="min-w-0">
            <div className="flex min-w-0 items-baseline gap-2">
              <Typography variant="label" className="min-w-0 truncate">
                {stage.label}
              </Typography>
              {stage.status === 'current' ? (
                <Loader2 className="size-4 shrink-0 animate-spin text-info" aria-hidden="true" />
              ) : stage.status === 'completed' ? (
                <CheckCircle2 className="size-4 shrink-0 text-success" aria-hidden="true" />
              ) : stage.status === 'failed' ? (
                <AlertCircle className="size-4 shrink-0 text-destructive" aria-hidden="true" />
              ) : null}
            </div>
            {stage.status === 'current' && stage.total > 1 ? (
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-info transition-all"
                  style={{ width: `${stage.percent}%` }}
                />
              </div>
            ) : null}
          </div>
          <Badge
            variant={
              stage.status === 'failed'
                ? 'destructive'
                : stage.status === 'completed'
                  ? 'success'
                  : stage.status === 'current'
                    ? 'info'
                    : 'secondary'
            }
          >
            {getStageStatusLabel(stage.status)}
          </Badge>
          <Typography variant="bodySmall" tone="muted" className="@3xl/fsn-status:text-right">
            {stage.total > 1 ? `${stage.processed}/${stage.total}` : ''}
          </Typography>
        </div>
      ))}
    </div>
  )
}

function FsnImportProgress({ attempt }) {
  const elapsedSeconds = useAttemptElapsedSeconds(attempt)

  if (!attempt) {
    return null
  }

  return (
    <section className="@container/fsn-status space-y-5">
      <div className="space-y-2">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <Typography variant="h4" className="pb-0">
              Import progress
            </Typography>
            <Typography variant="supporting" className="mt-1 block">
              {attempt.message ?? attempt.stage_label}
            </Typography>
          </div>
          <Badge
            variant={
              attempt.status === 'completed'
                ? 'success'
                : attempt.status === 'failed'
                  ? 'destructive'
                  : 'info'
            }
          >
            {attempt.stage_label}
          </Badge>
        </div>
      </div>
      <div className="space-y-5">
        <div className="h-2 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full rounded-full bg-primary transition-all"
            style={{ width: `${attempt.progress.percent}%` }}
          />
        </div>
        <div className="flex flex-wrap justify-between gap-3">
          <Typography variant="bodySmall" tone="muted">
            {attempt.progress.percent}% complete
          </Typography>
          <Typography variant="bodySmall" tone="muted">
            Elapsed {formatAttemptElapsedTime(elapsedSeconds)}
          </Typography>
          <Typography variant="bodySmall" tone="muted">
            {attempt.progress.processed}/{attempt.progress.total}
          </Typography>
        </div>
        <AttemptTimeline attempt={attempt} />
        {attempt.error?.message ? (
          <Alert variant="destructive">
            <AlertCircle className="size-4" />
            <AlertTitle>Import failed</AlertTitle>
            <AlertDescription>{attempt.error.message}</AlertDescription>
          </Alert>
        ) : null}
      </div>
    </section>
  )
}

export { formatAttemptElapsedTime, FsnImportProgress, getAttemptElapsedSeconds }
